import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/api/client';
import { farmScopeQuery } from '@/stores/farmScope';

/**
 * Data hooks for Financials & Ledgers.
 *
 * The finance routes carry no response DTOs — a trial balance's shape depends on
 * which report was asked for — so the typed client can only say `unknown` for
 * them. The shapes are declared here and asserted at that one boundary, rather
 * than letting `any` leak into ten screens.
 *
 * Every mutation invalidates the whole `fin` key. Finance figures are connected:
 * posting a bill moves a buyer's balance, the trial balance, the dashboard and the
 * ageing report, and working out which of those to refresh at each call site is how
 * a screen ends up showing yesterday's number beside today's.
 */

export interface Paged<T> {
  items: T[];
  total: number;
}

export interface AccountRow {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentId: string | null;
  isPostable: boolean;
  subledger: string | null;
  requiresFarm: boolean;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  version: number;
  ownDebit: string;
  ownCredit: string;
  totalDebit: string;
  totalCredit: string;
  closingBalance: string;
}

export interface PostableAccount {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  type: string;
  subledger: string | null;
  requiresFarm: boolean;
}

export interface MoneyPlaceRow {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  farmId: string | null;
  farmName: string;
  isActive: boolean;
  closingBalance: string;
  version: number;
  bankLabel?: string | null;
  branchLabel?: string | null;
  titleOnAccount?: string | null;
  accountNumber?: string | null;
  iban?: string | null;
}

export interface CategoryRow {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  isStockPurchase: boolean;
  isSystem: boolean;
  isActive: boolean;
  version: number;
  accountCode: string;
  accountName: string;
}

export interface PeriodRow {
  id: string;
  periodNo: number;
  label: string;
  startsOn: string;
  endsOn: string;
  status: 'OPEN' | 'CLOSED';
  reopened: boolean;
  version: number;
}

export interface YearRow {
  id: string;
  code: string;
  startsOn: string;
  endsOn: string;
  status: 'OPEN' | 'CLOSED';
  rolled: boolean;
  version: number;
  periods: PeriodRow[];
}

export interface BillRow {
  id: string;
  documentNo: string;
  status: string;
  billDate: string;
  buyerId: string;
  buyerName: string;
  farmId: string;
  farmName: string;
  flockId: string | null;
  flockCode: string | null;
  gatePassId: string | null;
  gatePassNo: string | null;
  bookGatePassNo: string | null;
  birdCount: number | null;
  netKg: string;
  ratePerKg: string;
  /** Guarded by fin.margin.read, so these two can legitimately be absent. */
  grossAmount?: string;
  netAmount?: string;
  deductions: string;
  deductionLines: Array<{
    id: string;
    kind: string;
    label: string | null;
    amount: string;
    accountCode: string | null;
  }>;
  paidAmount: string;
  outstanding: string;
  remarks: string | null;
  reversalReason: string | null;
  version: number;
}

export interface BillableDispatch {
  id: string;
  documentNo: string;
  bookGatePassNo: string | null;
  issuedOn: string;
  birdCount: number;
  netKg: string | null;
  buyerId: string;
  buyerName: string;
  farmId: string;
  farmName: string;
  flockId: string;
  flockCode: string;
}

export interface ReceiptRow {
  id: string;
  documentNo: string;
  status: string;
  receivedOn: string;
  buyerId: string;
  buyerName: string;
  entryAmount: string;
  instrument: string;
  bankCharge: string;
  chequeNo: string | null;
  reference: string | null;
  intoCashBoxId: string | null;
  intoBankAccountId: string | null;
  intoName: string | null;
  farmId: string | null;
  allocatedAmount: string;
  unappliedAmount: string;
  allocations: Array<{
    id: string;
    dispatchBillId: string;
    documentNo: string;
    billDate: string;
    lineAmount: string;
  }>;
  remarks: string | null;
  reversalReason: string | null;
  version: number;
}

export interface OpenBill {
  id: string;
  documentNo: string;
  billDate: string;
  netAmount: string;
  paidAmount: string;
  outstanding: string;
}

export interface VoucherRow {
  id: string;
  documentNo: string;
  status: string;
  direction: 'IN' | 'OUT';
  voucherDate: string;
  cashBoxId: string;
  cashBoxName: string;
  entryAmount: string;
  categoryId: string | null;
  categoryName: string | null;
  counterAccountCode: string | null;
  counterAccountName: string | null;
  flockId: string | null;
  flockCode: string | null;
  farmId: string | null;
  payee: string | null;
  narration: string | null;
  voucherNo: string | null;
  reversalReason: string | null;
  version: number;
}

export interface CashPage {
  cashBoxId: string;
  cashBoxName: string;
  from: string;
  to: string;
  openingBalance: string;
  closingBalance: string;
  items: Array<{
    entryDate: string;
    entryNo: string;
    narration: string;
    sourceType: string | null;
    sourceNo: string | null;
    moneyIn: string;
    moneyOut: string;
    closingBalance: string;
  }>;
}

export interface ExpenseRow {
  id: string;
  documentNo: string;
  status: string;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  expenseDate: string;
  farmId: string;
  farmName: string;
  categoryId: string;
  categoryName: string;
  flockId: string | null;
  flockCode: string | null;
  entryAmount: string;
  paidFromCashBoxId: string | null;
  paidFromBankAccountId: string | null;
  paidFromName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  unpaid: boolean;
  paidTo: string | null;
  billRef: string | null;
  remarks: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  reversalReason: string | null;
  version: number;
}

export interface JournalRow {
  id: string;
  entryNo: string;
  kind: string;
  status: string;
  entryDate: string;
  narration: string;
  sourceType: string | null;
  sourceNo: string | null;
  entryAmount: string;
  periodLabel: string;
  periodStatus: string;
  farmId: string | null;
  farmName: string | null;
  reversesEntryNo: string | null;
  isReversed: boolean;
}

export interface JournalDetail extends Omit<JournalRow, 'entryAmount' | 'isReversed'> {
  totalDebit: string;
  totalCredit: string;
  reversalReason: string | null;
  reversedByEntryNo: string | null;
  version: number;
  lines: Array<{
    id: string;
    lineNo: number;
    accountCode: string;
    accountName: string;
    debit: string;
    credit: string;
    narration: string | null;
    farmId: string | null;
    farmName: string | null;
    flockId: string | null;
    flockCode: string | null;
    partyType: string | null;
    partyId: string | null;
  }>;
}

export interface Overview {
  from: string;
  to: string;
  cashInHand: string;
  inTheBank: string;
  stillToReceive: string;
  stillToPay: string;
  salesThisPeriod: string;
  costsThisPeriod: string;
  toBeClassified: string;
}

export interface TrialBalance {
  asOn: string;
  balanced: boolean;
  totalDebit: string;
  totalCredit: string;
  items: Array<{
    code: string;
    nameEn: string;
    type: string;
    periodDebit: string;
    periodCredit: string;
    closingDebit: string;
    closingCredit: string;
  }>;
}

export interface ProfitAndLoss {
  from: string;
  to: string;
  totalIncome: string;
  totalExpense: string;
  netResult: string;
  items: Array<{
    code: string;
    nameEn: string;
    groupName: string | null;
    type: string;
    closingBalance: string;
  }>;
}

export interface BalanceSheet {
  asOn: string;
  balanced: boolean;
  totalAssets: string;
  totalLiabilities: string;
  totalEquity: string;
  resultNotYetRolled: string;
  assets: BalanceRow[];
  liabilities: BalanceRow[];
  equity: BalanceRow[];
}

export interface BalanceRow {
  code: string;
  nameEn: string;
  groupName: string | null;
  closingBalance: string;
}

export interface LedgerPage {
  from: string;
  to: string;
  openingBalance: string;
  periodDebit: string;
  periodCredit: string;
  closingBalance: string;
  items: Array<{
    entryDate: string;
    entryNo: string;
    accountCode: string;
    narration: string;
    sourceType: string | null;
    sourceNo: string | null;
    debit: string;
    credit: string;
    closingBalance: string;
  }>;
}

export interface AgeingRow {
  buyerId: string;
  buyerName: string;
  week: string;
  month: string;
  twoMonths: string;
  older: string;
  closingBalance: string;
}

export interface CostPerBirdRow {
  flockId: string;
  flockCode: string;
  flockName: string;
  birdsPlaced: number;
  /** Guarded by fin.margin.read. */
  costPerBird?: string | null;
  margin?: string;
  totalCost: string;
  totalRevenue: string;
  soldBirds: number | null;
  soldWeightKg: string | null;
  costPerKg: string | null;
}

export interface CloseBlocker {
  kind: string;
  what: string;
}

export interface FinanceSettingsRow {
  id: number;
  fiscalYearStartMonth: number;
  salesAccountCode: string | null;
  receivableAccountCode: string | null;
  roundingAccountCode: string | null;
  suspenseAccountCode: string | null;
  autoPostInventory: boolean;
  autoPostPayroll: boolean;
  version: number;
}

const get = <T,>(path: string, query?: Record<string, string | number | undefined>) =>
  api.get(path as never, query) as Promise<T>;

export function useFinanceInvalidation() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['fin'] });
    // A bill is raised from a dispatch and shows on it, so the sales caches move too.
    void queryClient.invalidateQueries({ queryKey: ['sd'] });
  }, [queryClient]);
}

/** One mutation hook for every finance write; see the note at the top. */
export function useFinanceMutation<T>() {
  const invalidate = useFinanceInvalidation();
  return useMutation<T, Error, () => Promise<T>>({
    mutationFn: (action) => action(),
    onSuccess: invalidate,
  });
}

// --------------------------------------------------------------------- setup

export function useAccounts() {
  return useQuery({
    queryKey: ['fin', 'accounts'],
    queryFn: () => get<{ items: AccountRow[] }>('/api/v1/fin/accounts'),
  });
}

export function usePostableAccounts(type?: string) {
  return useQuery({
    queryKey: ['fin', 'accounts', 'postable', type],
    queryFn: () =>
      get<{ items: PostableAccount[] }>('/api/v1/fin/accounts/postable', {
        ...(type ? { type } : {}),
      }),
  });
}

export function useCashBoxes(farmIds: readonly string[] = []) {
  return useQuery({
    queryKey: ['fin', 'cash-boxes', farmIds],
    queryFn: () =>
      get<{ items: MoneyPlaceRow[] }>('/api/v1/fin/cash-boxes', farmScopeQuery(farmIds)),
  });
}

export function useBankAccounts(farmIds: readonly string[] = []) {
  return useQuery({
    queryKey: ['fin', 'bank-accounts', farmIds],
    queryFn: () =>
      get<{ items: MoneyPlaceRow[] }>(
        '/api/v1/fin/bank-accounts',
        farmScopeQuery(farmIds),
      ),
  });
}

export function useExpenseCategories(includeInactive = false) {
  return useQuery({
    queryKey: ['fin', 'expense-categories', includeInactive],
    queryFn: () =>
      get<{ items: CategoryRow[] }>('/api/v1/fin/expense-categories', {
        ...(includeInactive ? { includeInactive: 'true' } : {}),
      }),
  });
}

export function usePeriods() {
  return useQuery({
    queryKey: ['fin', 'periods'],
    queryFn: () => get<{ items: YearRow[] }>('/api/v1/fin/periods'),
  });
}

export function useCloseReadiness(periodId: string | null) {
  return useQuery({
    queryKey: ['fin', 'periods', periodId, 'readiness'],
    queryFn: () => get<CloseBlocker[]>(`/api/v1/fin/periods/${periodId}/readiness`),
    enabled: Boolean(periodId),
  });
}

export function useFinanceSettings() {
  return useQuery({
    queryKey: ['fin', 'settings'],
    queryFn: () => get<FinanceSettingsRow>('/api/v1/fin/settings'),
  });
}

// ------------------------------------------------------------------- billing

export function useBills(filters: {
  farmIds: readonly string[];
  buyerId?: string;
  status?: string;
  search?: string;
  take: number;
  skip: number;
}) {
  return useQuery({
    queryKey: ['fin', 'bills', filters],
    queryFn: () =>
      get<Paged<BillRow>>('/api/v1/fin/bills', {
        ...farmScopeQuery(filters.farmIds),
        ...(filters.buyerId ? { buyerId: filters.buyerId } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

export function useBillableDispatches(farmIds: readonly string[], buyerId?: string) {
  return useQuery({
    queryKey: ['fin', 'billable', farmIds, buyerId],
    queryFn: () =>
      get<{ items: BillableDispatch[] }>('/api/v1/fin/bills/billable', {
        ...farmScopeQuery(farmIds),
        ...(buyerId ? { buyerId } : {}),
      }),
  });
}

export function useLastRate(buyerId: string) {
  return useQuery({
    queryKey: ['fin', 'last-rate', buyerId],
    queryFn: () =>
      get<{ ratePerKg: string | null; billDate: string | null; documentNo: string | null }>(
        '/api/v1/fin/bills/last-rate',
        { buyerId },
      ),
    enabled: Boolean(buyerId),
  });
}

// ------------------------------------------------------------------ receipts

export function useReceipts(filters: {
  buyerId?: string;
  status?: string;
  take: number;
  skip: number;
}) {
  return useQuery({
    queryKey: ['fin', 'receipts', filters],
    queryFn: () =>
      get<Paged<ReceiptRow>>('/api/v1/fin/receipts', {
        ...(filters.buyerId ? { buyerId: filters.buyerId } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

export function useOpenBills(buyerId: string) {
  return useQuery({
    queryKey: ['fin', 'open-bills', buyerId],
    queryFn: () =>
      get<{ items: OpenBill[] }>('/api/v1/fin/receipts/open-bills', { buyerId }),
    enabled: Boolean(buyerId),
  });
}

// ----------------------------------------------------------------- cash book

export function useCashPage(cashBoxId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['fin', 'cash-book', cashBoxId, from, to],
    queryFn: () => get<CashPage>('/api/v1/fin/cash-book', { cashBoxId, from, to }),
    enabled: Boolean(cashBoxId && from && to),
  });
}

export function useCashVouchers(filters: {
  cashBoxId?: string;
  direction?: string;
  status?: string;
  take: number;
  skip: number;
}) {
  return useQuery({
    queryKey: ['fin', 'cash-vouchers', filters],
    queryFn: () =>
      get<Paged<VoucherRow>>('/api/v1/fin/cash-vouchers', {
        ...(filters.cashBoxId ? { cashBoxId: filters.cashBoxId } : {}),
        ...(filters.direction ? { direction: filters.direction } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

export function useCashInAccounts() {
  return useQuery({
    queryKey: ['fin', 'cash-in-accounts'],
    queryFn: () => get<{ codes: string[] }>('/api/v1/fin/cash-vouchers/in-accounts'),
  });
}

// ------------------------------------------------------------------ expenses

export function useExpenses(filters: {
  farmIds: readonly string[];
  approvalStatus?: string;
  status?: string;
  take: number;
  skip: number;
}) {
  return useQuery({
    queryKey: ['fin', 'expenses', filters],
    queryFn: () =>
      get<Paged<ExpenseRow>>('/api/v1/fin/expenses', {
        ...farmScopeQuery(filters.farmIds),
        ...(filters.approvalStatus ? { approvalStatus: filters.approvalStatus } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

// ------------------------------------------------------------------- journal

export function useJournal(filters: {
  kind?: string;
  search?: string;
  take: number;
  skip: number;
}) {
  return useQuery({
    queryKey: ['fin', 'journal', filters],
    queryFn: () =>
      get<Paged<JournalRow>>('/api/v1/fin/journal', {
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

export function useJournalEntry(id: string | null) {
  return useQuery({
    queryKey: ['fin', 'journal', id],
    queryFn: () => get<JournalDetail>(`/api/v1/fin/journal/${id}`),
    enabled: Boolean(id),
  });
}

// ------------------------------------------------------------------- reports

export function useOverview() {
  return useQuery({
    queryKey: ['fin', 'overview'],
    queryFn: () => get<Overview>('/api/v1/fin/reports/overview'),
  });
}

export function useTrialBalance(asOn: string, farmId?: string) {
  return useQuery({
    queryKey: ['fin', 'trial-balance', asOn, farmId],
    queryFn: () =>
      get<TrialBalance>('/api/v1/fin/reports/trial-balance', {
        ...(asOn ? { asOn } : {}),
        ...(farmId ? { farmId } : {}),
      }),
  });
}

export function useProfitAndLoss(from: string, to: string, farmId?: string) {
  return useQuery({
    queryKey: ['fin', 'profit-and-loss', from, to, farmId],
    queryFn: () =>
      get<ProfitAndLoss>('/api/v1/fin/reports/profit-and-loss', {
        from,
        to,
        ...(farmId ? { farmId } : {}),
      }),
    enabled: Boolean(from && to),
  });
}

export function useBalanceSheet(asOn: string, farmId?: string) {
  return useQuery({
    queryKey: ['fin', 'balance-sheet', asOn, farmId],
    queryFn: () =>
      get<BalanceSheet>('/api/v1/fin/reports/balance-sheet', {
        ...(asOn ? { asOn } : {}),
        ...(farmId ? { farmId } : {}),
      }),
  });
}

export function useLedger(params: {
  accountCode?: string;
  partyType?: string;
  partyId?: string;
  from: string;
  to: string;
}) {
  return useQuery({
    queryKey: ['fin', 'ledger', params],
    queryFn: () =>
      get<LedgerPage>('/api/v1/fin/reports/ledger', {
        from: params.from,
        to: params.to,
        ...(params.accountCode ? { accountCode: params.accountCode } : {}),
        ...(params.partyType ? { partyType: params.partyType } : {}),
        ...(params.partyId ? { partyId: params.partyId } : {}),
      }),
    enabled: Boolean((params.accountCode || params.partyId) && params.from && params.to),
  });
}

export function useAgeing(asOn: string) {
  return useQuery({
    queryKey: ['fin', 'ageing', asOn],
    queryFn: () =>
      get<{ asOn: string; items: AgeingRow[] }>('/api/v1/fin/reports/receivable-ageing', {
        ...(asOn ? { asOn } : {}),
      }),
  });
}

export function useCostPerBird(flockId?: string) {
  return useQuery({
    queryKey: ['fin', 'cost-per-bird', flockId],
    queryFn: () =>
      get<{ items: CostPerBirdRow[] }>('/api/v1/fin/reports/cost-per-bird', {
        ...(flockId ? { flockId } : {}),
      }),
  });
}

export function useBuyerStatement(buyerId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['fin', 'buyer-statement', buyerId, from, to],
    queryFn: () =>
      get<LedgerPage & { buyer: { id: string; code: string; nameEn: string } }>(
        '/api/v1/fin/reports/buyer-statement',
        { buyerId, from, to },
      ),
    enabled: Boolean(buyerId && from && to),
  });
}

/** How a deduction reads on a bill. */
export const DEDUCTION_LABELS: Record<string, string> = {
  COMMISSION: 'Commission',
  SHORTAGE: 'Weight shortage',
  FREIGHT: 'Freight and cartage',
  LOADING: 'Loading labour',
  OTHER: 'Other',
};

export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  EQUITY: 'Capital',
  INCOME: 'Income',
  EXPENSE: 'Costs',
};
